import { TwilioVerificationChannelEnum, UserRoleIDEnum, useToggle, UseToggleType } from '@alluradirect/react';
import { ReactNode } from 'react';
import { ChevronRight } from '@mui/icons-material';
import { Link, Typography } from '@mui/material';
import Box from '@mui/system/Box';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import { AppLink } from 'components/core/AppLink/AppLink';
import ConfirmationDialog from 'components/core/ConfirmationDialog/ConfirmationDialog';
import Grid from 'components/core/Grid';
import { LoadingButton } from 'components/core/LoadingButton/LoadingButton';
import { LoadingIndicator } from 'components/core/LoadingIndicator/LoadingIndicator';
import { FormikAppCheckboxWithLabel } from 'components/formik/core/FormikAppCheckboxWithLabel/FormikAppCheckboxWithLabel';
import { FormikAppEmailCheckTaken } from 'components/formik/core/FormikAppEmail/FormikAppEmail';
import { FormikAppTextField } from 'components/formik/core/FormikAppTextField/FormikAppTextField';
import { SignupCreateAccountFormikValues } from 'components/views/pages/signup/SignupCreateAccount';
import { VerificationDialog, VerificationStepEnum } from 'components/views/verification/VerificationDialog';
import { AppRoutes } from 'config/appRoutes';
import { IS_DIRECT_VACATIONS } from 'config/envSetup';
import { httpsUrl } from 'config/url';
import { useFormikContext } from 'formik';
import { useTermsOfServiceHTMLQuery } from 'hooks/api/core/useTermsOfServiceHTMLQuery';

interface Props {
  children?: ReactNode;
  onVerificationSuccess: (data: SignupCreateAccountFormikValues, setSubmitting: any) => Promise<void>;
  toggleEmailVerification: UseToggleType;
  hasTopAppDivider?: boolean;
  hasFooter?: boolean;
  isEmailFieldDisabled?: boolean;
  isCaptchaValid?: boolean;
}

export const SignupCreateAccountForm = ({
  children,
  onVerificationSuccess,
  toggleEmailVerification,
  hasTopAppDivider = true,
  hasFooter = true,
  isEmailFieldDisabled = false,
  isCaptchaValid = false,
}: Props) => {
  const termsQuery = useTermsOfServiceHTMLQuery({
    user_type_id: UserRoleIDEnum.guest,
    is_direct_vacations: IS_DIRECT_VACATIONS(),
  });

  const { values, setSubmitting, isSubmitting, isValid } = useFormikContext<SignupCreateAccountFormikValues>();
  const toggleTermsDialog = useToggle();

  return (
    <>
      <Grid container spacing={2}>
        {hasTopAppDivider ? (
          <Grid item xs={12}>
            <AppDivider />
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <FormikAppEmailCheckTaken name="email" disabled={isEmailFieldDisabled} />
        </Grid>

        {values.is_email_taken ? null : (
          <>
            <Grid item xs={12}>
              <FormikAppTextField name="first_name" label={'First Name'} />
            </Grid>
            <Grid item xs={12}>
              <FormikAppTextField name="last_name" label={'Last Name'} />
            </Grid>

            <Grid item xs={12}>
              <AppDivider />
            </Grid>

            <Grid item xs={12}>
              <FormikAppTextField name="password" label="Password" isPasswordField />
            </Grid>
            <Grid item xs={12}>
              {/* Render the Turnstile CAPTCHA here */}
              {children}
            </Grid>

            <Grid item xs={12}>
              <FormikAppCheckboxWithLabel
                name="marketing_emails"
                label="Include me on member-only promotions and discounts offers."
                labelPlacement="end"
                formControlProps={{
                  componentsProps: {
                    typography: {
                      variant: 'caption',
                    },
                  },
                  sx: {
                    userSelect: 'none',
                  },
                }}
              />
            </Grid>
          </>
        )}

        {/* <Grid item xs={12}></Grid> */}

        <Grid item xs={12}>
          <LoadingButton
            isDisabled={values.is_email_taken || !isCaptchaValid}
            isLoading={isSubmitting}
            text="continue"
            isFullWidth
            isFormikButton={isValid}
            endIcon={<ChevronRight />}
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" flexDirection={'column'}>
            <Typography variant="caption" sx={{ display: 'inline-block' }}>
              By creating an account, you are accepting alluraDirect&apos;s{' '}
              <Link
                variant="caption"
                color="secondary"
                onClick={toggleTermsDialog.open}
                href="#"
                sx={{ display: 'inline-block' }}
              >
                terms and conditions
              </Link>{' '}
              and{' '}
              <AppLink href={`${httpsUrl()}/privacy.cfm`} openInNewTab>
                privacy policy
              </AppLink>
            </Typography>
          </Box>
        </Grid>

        {hasFooter ? (
          <>
            <Grid item xs={12}>
              <AppDivider />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" textAlign={'center'}>
                Already have an account? <AppLink href={AppRoutes.loginGuest}>Log In</AppLink>
              </Typography>
            </Grid>
          </>
        ) : null}
      </Grid>

      {toggleTermsDialog.isOpen ? (
        <ConfirmationDialog
          isDialogOpen={toggleTermsDialog.isOpen}
          handleCloseDialog={toggleTermsDialog.close}
          dialogTitle="Terms & Conditions"
          hasBottomBar={false}
        >
          {termsQuery.data ? (
            <div dangerouslySetInnerHTML={{ __html: termsQuery.data.DATA.TOSSA }}></div>
          ) : (
            <LoadingIndicator height={'80vh'} />
          )}
        </ConfirmationDialog>
      ) : null}

      {toggleEmailVerification.isOpen ? (
        <VerificationDialog
          isOpen={toggleEmailVerification.isOpen}
          handleClose={toggleEmailVerification.close}
          initialData={values.email}
          channel={TwilioVerificationChannelEnum.email}
          onVerifySuccess={() => onVerificationSuccess(values, setSubmitting)}
          initialStep={VerificationStepEnum.VerifyCode}
          createAccountData={values}
        />
      ) : null}
    </>
  );
};
